import apiClient, { refreshAccessToken } from './api';
import { AuthResponse, LoginRequest, RegisterRequest, User } from '@/types';
import { devLog } from '@/utils/logger';

/**
 * Authentication Service
 *
 * Handles all authentication-related API calls:
 * - User registration
 * - User login
 * - User logout
 * - Token refresh
 * - Get current user
 *
 * Note: Backend wraps all responses in { success, data, message }
 * so we need to extract the nested data
 */

// Backend wraps responses in this structure
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
}

/**
 * Request deduplication: Prevent concurrent duplicate requests
 */
let currentUserPromise: Promise<User> | null = null;

export const authService = {
  /**
   * Register a new user
   */
  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/api/auth/register', data);
    return response.data.data; // Extract nested data
  },

  /**
   * Login user
   */
  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/api/auth/login', data);
    devLog.log('[authService] Login response:', response.data);
    return response.data.data; // Extract nested data
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    await apiClient.post('/api/auth/logout');
  },

  /**
   * Refresh access token
   * Uses unified refresh function from api.ts to prevent race conditions
   */
  async refreshToken(): Promise<{ accessToken: string }> {
    const accessToken = await refreshAccessToken();
    return { accessToken };
  },

  /**
   * Get current authenticated user
   * Deduplicated: Concurrent calls will share the same promise
   */
  async getCurrentUser(): Promise<User> {
    // If already fetching, return the existing promise
    if (currentUserPromise) {
      devLog.log('[authService] getCurrentUser already in progress, reusing existing promise');
      return currentUserPromise;
    }

    // Create new fetch promise
    currentUserPromise = (async () => {
      try {
        const response = await apiClient.get<ApiResponse<User>>('/api/auth/me');
        return response.data.data; // Extract nested data
      } finally {
        // Clear the promise after completion (success or failure)
        currentUserPromise = null;
      }
    })();

    return currentUserPromise;
  },

  /**
   * Request a password reset email
   */
  async requestPasswordReset(email: string): Promise<void> {
    await apiClient.post('/api/auth/forgot-password', { email });
  },

  /**
   * Change user password
   */
  async changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>('/api/profile/change-password', {
      currentPassword,
      newPassword,
      confirmPassword
    });
    return response.data;
  },
};
